"use client";

import { useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";

import { useAnalytics } from "@/components/analytics/use-analytics";
import { estimateMonthlyUsage } from "@/components/pricing-calculator";
import { usePricingCatalog } from "@/hooks/usePricingCatalog";
import { formatCurrency } from "@/lib/format-currency";

// The cadences the /pricing estimator offers, as checks per day. A month is
// 30 days, like the home page calculator.
const FREQUENCIES = [
  { key: "daily", checksPerDay: 1 },
  { key: "every6h", checksPerDay: 4 },
  { key: "hourly", checksPerDay: 24 },
] as const;

type FrequencyKey = (typeof FREQUENCIES)[number]["key"];

const MAX_SOURCES = 50;
/** 2, not the 1.62 measured in production: see `pagesPerCheck` in `pricing-calculator.tsx`. */
const DEFAULT_PAGES_PER_CHECK = 2;

/**
 * The /pricing page's cost estimator (#3523): sources, pages per check and a
 * cadence in, monthly checks, credits and cost out. The math is the home
 * calculator's `estimateMonthlyUsage`; prices and the per-check ceiling come
 * from the billing catalog.
 */
export default function PricingEstimator() {
  const locale = useLocale();
  const t = useTranslations("pricingPage.estimator");
  const pricing = usePricingCatalog(locale);
  const { trackEvent } = useAnalytics();
  const tracked = useRef(false);
  const [sources, setSources] = useState(5);
  const [pagesPerCheck, setPagesPerCheck] = useState(DEFAULT_PAGES_PER_CHECK);
  const [frequency, setFrequency] = useState<FrequencyKey>("daily");

  const checksPerDay =
    FREQUENCIES.find((option) => option.key === frequency)?.checksPerDay ?? 1;
  const usage = estimateMonthlyUsage({
    urls: sources,
    checksPerUrl: checksPerDay * 30,
    pagesPerCheck,
    perCheckMajor: pricing.perCheckMajor,
  });
  // Every control sets its value and reports the first interaction once.
  const tracking =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      if (tracked.current) return;
      tracked.current = true;
      trackEvent("pricing_calculator_used", { location: "pricing_page" });
    };

  return (
    <section id="estimate" className="border-y border-border bg-muted/40">
      <div className="container mx-auto grid max-w-5xl items-start gap-10 px-4 py-16 md:px-6 lg:grid-cols-2">
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {t("eyebrow")}
          </p>
          <h2 className="text-3xl font-bold tracking-tight text-foreground">
            {t("title")}
          </h2>
          <p className="max-w-md text-base leading-relaxed text-muted-foreground">
            {/* No unit price here: "month" follows within 200 characters (see pricing/page.tsx). */}
            {t("description")}
          </p>
        </div>
        <div className="flex flex-col gap-6 rounded-xl border border-border bg-card p-6">
          <RangeField
            id="estimate-sources"
            label={t("sources")}
            value={sources}
            max={MAX_SOURCES}
            onChange={tracking(setSources)}
          />
          <RangeField
            id="estimate-pages"
            label={t("pages")}
            value={pagesPerCheck}
            max={pricing.maxCheckCredits ?? DEFAULT_PAGES_PER_CHECK}
            hint={
              pricing.loading
                ? t("pagesHintLoading")
                : t("pagesHint", pricing.pricingValues)
            }
            onChange={tracking(setPagesPerCheck)}
          />
          <FrequencyPicker
            value={frequency}
            onChange={tracking(setFrequency)}
          />
          <EstimateResult
            usage={usage}
            currency={pricing.currency}
            credits={pricing.credits}
            creditsPerPack={pricing.pricingValues.creditsPerPack}
          />
        </div>
      </div>
    </section>
  );
}

function RangeField({
  id,
  label,
  value,
  max,
  hint,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  max: number;
  hint?: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="flex justify-between gap-4 text-sm">
        <span className="font-semibold text-foreground">{label}</span>
        <span className="font-semibold tabular-nums text-primary">{value}</span>
      </label>
      <input
        id={id}
        type="range"
        min={1}
        max={max}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="w-full accent-primary"
      />
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function FrequencyPicker({
  value,
  onChange,
}: {
  value: FrequencyKey;
  onChange: (value: FrequencyKey) => void;
}) {
  const t = useTranslations("pricingPage.estimator");
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-sm font-semibold text-foreground">
        {t("frequency")}
      </legend>
      <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
        {FREQUENCIES.map(({ key }) => (
          <button
            key={key}
            type="button"
            aria-pressed={key === value}
            onClick={() => onChange(key)}
            className={
              key === value
                ? "rounded-md bg-background px-1.5 py-2 text-sm font-semibold text-foreground shadow-sm"
                : "rounded-md px-1.5 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
            }
          >
            {t(`frequencies.${key}`)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function EstimateResult({
  usage,
  currency,
  credits,
  creditsPerPack,
}: {
  usage: ReturnType<typeof estimateMonthlyUsage>;
  currency: string | undefined;
  credits: number | undefined;
  creditsPerPack: string;
}) {
  const locale = useLocale();
  const t = useTranslations("pricingPage.estimator");
  const packs = credits ? Math.ceil(usage.pages / credits) : 0;

  return (
    <div className="flex flex-col gap-4 border-t border-border pt-5">
      <dl className="grid grid-cols-[1fr_auto] gap-y-2 text-sm">
        <dt className="text-muted-foreground">{t("checks")}</dt>
        <dd className="font-semibold tabular-nums text-foreground">
          {usage.checks.toLocaleString(locale)}
        </dd>
        <dt className="text-muted-foreground">{t("credits")}</dt>
        <dd className="font-semibold tabular-nums text-foreground">
          {usage.pages.toLocaleString(locale)}
        </dd>
      </dl>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-0.5" aria-live="polite">
          <span className="text-xs text-muted-foreground">{t("cost")}</span>
          {usage.cost === null || !currency ? (
            <span className="text-base text-muted-foreground">
              {t("costLoading")}
            </span>
          ) : (
            <span className="text-4xl font-bold tracking-tight tabular-nums text-foreground">
              {formatCurrency(usage.cost, locale, currency)}
            </span>
          )}
        </div>
        {packs > 0 ? (
          <span className="text-sm text-muted-foreground">
            {t("packs", { count: packs, creditsPerPack })}
          </span>
        ) : null}
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        {t("note")}
      </p>
    </div>
  );
}
